import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  Gamepad2, Edit3, X, Loader2, Star, Flame, Sparkles,
  ArrowRight, Download, Search, Radio, LogOut, Layers, Power, PowerOff,
  ArrowUp, ArrowDown, ArrowUpToLine, Upload, Trash2, Image as ImageIcon,
  ChevronLeft, ChevronRight, ChevronDown,
  LayoutGrid, Rows3, SlidersHorizontal, RotateCcw, Link2, Boxes,
  CircleCheck, CircleSlash, EyeOff, KeyRound, Settings2, Server,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import {
  GAME_ARTWORK_ACCEPT, UPLOAD_MAX_LABEL, uploadTooLarge,
} from '../lib/mediaAsset';
import MediaThumb from '../components/MediaThumb';
import { useToast } from '../hooks/use-toast';
import PageSizeSelect from './components/PageSizeSelect';
import { PAGE_SIZES, readPageSize, writePageSize } from './lib/pagination';
import './games.css';

const empty = {
  name: '', provider: '', category: 'slot', image: '', api_image: '', image_override: '',
  active: true,
  featured: false, hot: false, new: false, order: 0,
  api_provider: '', provider_id: '', game_id: '', demo_supported: false,
  launch_url: '',
};
// Modal içi reel/demo seçiminin taslak başlangıcı (hiç eşleşme yok).
const EMPTY_TWIN_DRAFT = { primaryId: null, demoGameId: null, unlink: false };
// Toplu eşleştirmenin kaynak tercihi. Boş değer "otomatik" demektir: reel
// sağlayıcı önceliğine, demo da demo verebilen ilk kopyaya bırakılır.
const EMPTY_LINK_POLICY = { reel_source: '', demo_source: '' };
const AUTO_SOURCE_LABEL = 'Otomatik (öncelik sırası)';
// Site sırası: 1, 2, 3… verilenler önce, sırasız (0) oyunlar sonda — vitrin
// sorgusunun kuralının aynısı (backend `Cursor.ZERO_LAST`).
export const compareGameOrder = (a, b) => {
  const left = Number(a.order || 0);
  const right = Number(b.order || 0);
  if ((left > 0) !== (right > 0)) return left > 0 ? -1 : 1;
  return left - right;
};
const cats = [
  { id: 'slot', name: 'Slot' },
  { id: 'live-casino', name: 'Canlı Casino' },
  { id: 'crash', name: 'Crash' },
];
const liveGameTypes = [
  { id: 'all', name: 'Tüm Canlı Masalar', keywords: [] },
  { id: 'roulette', name: 'Rulet', keywords: ['roulette', 'rulet'] },
  { id: 'blackjack', name: 'Blackjack', keywords: ['blackjack'] },
  { id: 'baccarat', name: 'Baccarat', keywords: ['baccarat'] },
  {
    id: 'shows',
    name: 'Game Shows',
    keywords: ['crazy', 'monopoly', 'catcher', 'mega ball', 'deal or no deal', 'candy'],
  },
];
// Ana sayfa vitrin şeritleri: her sekme bir oyun etiketini yönetir.
// Panel seçimleri şeridin başına yerleşir; kalan alanı uç otomatik doldurur
// (/games/showcase). Böylece seçim sayısı ekran kapasitesinden az olsa da boşluk
// kalmaz.
const showcaseTabs = [
  {
    id: 'featured',
    flag: 'featured',
    name: 'Öne Çıkanlar',
    icon: Star,
    hint: 'Ana sayfalardaki öne çıkan oyun alanları; Vega’da banner altındaki kareler.',
    auto: 'Seçimler önce gelir; Vega karelerindeki kalan yerler rastgele açık oyunlarla tamamlanır.',
  },
  {
    id: 'popular',
    flag: 'hot',
    name: 'Popüler',
    icon: Flame,
    hint: 'Ana sayfadaki "Popüler Slotlar" şeridi ve menüdeki "En Popülerler".',
    auto: 'Seçim yapılmazsa son 30 günün oynanma sayısı kullanılır.',
  },
  {
    id: 'new',
    flag: 'new',
    name: 'Yeni',
    icon: Sparkles,
    hint: 'Ana sayfadaki "Yeni Eklenenler" şeridi ve menüdeki "Yeni Eklenenler".',
    auto: 'Seçim yapılmazsa katalogdaki en son eklenen oyunlar gösterilir.',
  },
];
const money = (v) => `${Number(v || 0).toFixed(2)} ₺`;
// Oyun listesinin varsayılan sayfa boyutu; yönetici listenin altındaki
// kutudan değiştirir ve seçim tarayıcısında kalır (bkz. lib/pagination).
const GAME_PAGE_SIZE = 100;
const GAME_SEARCH_DELAY_MS = 300;
const emptyShowcaseItems = { featured: [], hot: [], new: [] };
const emptyShowcaseCounts = { featured: 0, hot: 0, new: 0 };

const Games = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(
    () => readPageSize('games', GAME_PAGE_SIZE));
  const [providers, setProviders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cat, setCat] = useState('slot');
  const [provFilter, setProvFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [syncing, setSyncing] = useState('');
  const [providerOrderBusy, setProviderOrderBusy] = useState(false);
  const [gameOrderBusy, setGameOrderBusy] = useState(false);
  const [liveGameType, setLiveGameType] = useState('all');
  // Ana sayfa vitrin panelinde açık olan şerit: featured | popular | new
  const [showcaseTab, setShowcaseTab] = useState('featured');
  const [showcaseItems, setShowcaseItems] = useState(emptyShowcaseItems);
  const [showcaseCounts, setShowcaseCounts] = useState(emptyShowcaseCounts);
  // games | providers | labels | sessions
  const [view, setView] = useState('games');
  // Katalog kartlarla mı satırlarla mı çizilsin: 100 oyunluk sayfada satır
  // görünümü taramayı kolaylaştırır. Tercih tarayıcıda kalır — bir
  // yöneticinin seçimi diğerininkini değiştirmemelidir.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('admin:games-view-mode') === 'list' ? 'list' : 'grid';
    } catch { return 'grid'; }
  });
  // Düzenleme penceresinin açık sekmesi: settings | media | link
  const [modalTab, setModalTab] = useState('settings');
  // Kategori kontrolü bölümü varsayılan kapalıdır; yer kaplamasın diye
  // yönetici gerektiğinde açar. Tercih localStorage'da hatırlanır.
  const [categoriesOpen, setCategoriesOpen] = useState(() => {
    try { return localStorage.getItem('admin:games-cat-open') === '1'; } catch { return false; }
  });
  // Reel/Demo eşleştirmesi de kapalı başlar; "Çiftleri Birleştir" tehlikeli
  // toplu işlem olduğu için kazara tıklanmasın diye panel kapalıyken gizlidir.
  const [linkPanelOpen, setLinkPanelOpen] = useState(() => {
    try { return localStorage.getItem('admin:games-link-open') === '1'; } catch { return false; }
  });
  const [keysBusy, setKeysBusy] = useState(false);
  // Ana sayfa vitrini paneli varsayılan kapalıdır; yönetici açarsa tercihi
  // localStorage'da hatırlanır.
  const [showcaseOpen, setShowcaseOpen] = useState(() => {
    try { return localStorage.getItem('admin:games-showcase-open') === '1'; } catch { return false; }
  });
  // Vitrindeki sağlayıcı çipleri: slug başına görünen ad, logo, sıra.
  const [labels, setLabels] = useState([]);
  const [labelDrafts, setLabelDrafts] = useState({});
  const [labelBusy, setLabelBusy] = useState('');
  // Reel/demo eşleştirmesi (çift kayıt): demo durumu filtresi, gizli demo
  // kaynaklarını göster, birleştirme özeti ve modal içi kopya listesi.
  const [demoState, setDemoState] = useState('all'); // all | has | missing
  const [showMerged, setShowMerged] = useState(false);
  const [linkSummary, setLinkSummary] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  // Reel ve demo kaynağı panelden seçilir (eskiden reel Gold Palace, demo
  // Drakon'a sabitti). Taslak, kaydedilmiş tercihten başlar; kaydetmeden
  // çıkan yönetici hiçbir şeyi değiştirmemiş olur.
  const [linkPolicyDraft, setLinkPolicyDraft] = useState(null);
  const [policyBusy, setPolicyBusy] = useState(false);
  const [twins, setTwins] = useState([]);
  const [twinsKey, setTwinsKey] = useState('');
  // Yönetici bu grubun eşleşmesini çözdüyse kopyalar bilerek ayrı durur ve
  // uzlaştırma onları birleştirmez (backend ``link_split``).
  const [twinsSplit, setTwinsSplit] = useState(false);
  const [twinsBusy, setTwinsBusy] = useState(false);
  // Reel/demo seçimi form gibi davranır: düğmeler yalnızca taslağı değiştirir,
  // istek ancak Kaydet'e basılınca gider. (Anında uygulanması, pencereyi
  // kapatmakla vazgeçilemeyen bir değişiklik bırakıyordu.)
  const [twinDraft, setTwinDraft] = useState(EMPTY_TWIN_DRAFT);
  const [twinOriginal, setTwinOriginal] = useState(EMPTY_TWIN_DRAFT);
  const gameRequestId = useRef(0);
  const showcaseRequestId = useRef(0);
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(q.trim());
      setPage(0);
    }, GAME_SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [q]);

  const loadGames = useCallback(async () => {
    const requestId = gameRequestId.current + 1;
    gameRequestId.current = requestId;
    setLoading(true);
    setError('');
    try {
      const params = {
        category: cat,
        skip: page * pageSize,
        limit: pageSize,
      };
      if (provFilter !== 'all') {
        const [apiProvider, rawProviderId] = provFilter.split(':', 2);
        params.api_provider = apiProvider;
        const providerId = Number(rawProviderId);
        if (Number.isInteger(providerId) && providerId > 0) params.provider_id = providerId;
      }
      if (statusFilter !== 'all') params.status = statusFilter;
      if (demoState !== 'all') params.demo_state = demoState;
      if (showMerged) params.include_merged = true;
      if (searchTerm) params.q = searchTerm;
      if (cat === 'live-casino' && liveGameType !== 'all') {
        const selectedType = liveGameTypes.find((type) => type.id === liveGameType);
        if (selectedType?.keywords.length) params.keywords = selectedType.keywords.join(',');
      }
      const { data } = await client.get('/admin/games', {
        params,
      });
      if (requestId !== gameRequestId.current) return;
      const nextTotal = Number(data.total || 0);
      const lastPage = Math.max(0, Math.ceil(nextTotal / pageSize) - 1);
      setTotal(nextTotal);
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setItems(data.items || []);
    } catch (requestError) {
      if (requestId !== gameRequestId.current) return;
      setError(requestError.response?.data?.detail || 'Oyunlar alınamadı');
    }
    finally {
      if (requestId === gameRequestId.current) setLoading(false);
    }
  }, [cat, demoState, liveGameType, page, pageSize, provFilter, searchTerm,
      showMerged, statusFilter]);

  const loadShowcase = useCallback(async () => {
    const requestId = showcaseRequestId.current + 1;
    showcaseRequestId.current = requestId;
    try {
      const responses = await Promise.all(showcaseTabs.map((tab) => client.get('/admin/games', {
        params: { category: cat, status: tab.flag, skip: 0, limit: 2000 },
      })));
      if (requestId !== showcaseRequestId.current) return;
      const nextItems = { ...emptyShowcaseItems };
      const nextCounts = { ...emptyShowcaseCounts };
      showcaseTabs.forEach((tab, index) => {
        const data = responses[index].data || {};
        nextItems[tab.flag] = data.items || [];
        nextCounts[tab.flag] = Number(data.total || 0);
      });
      setShowcaseItems(nextItems);
      setShowcaseCounts(nextCounts);
    } catch {
      if (requestId !== showcaseRequestId.current) return;
      setShowcaseItems({ ...emptyShowcaseItems });
      setShowcaseCounts({ ...emptyShowcaseCounts });
    }
  }, [cat]);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/games/providers');
      setProviders(data.items || []);
    } catch { /* sağlayıcı listesi henüz senkronize edilmemiş olabilir */ }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/games/categories');
      setCategories(data.items || []);
    } catch { /* kategori bilgisi alınamadı */ }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/games/sessions', { params: { status: 'active' } });
      setSessions(data.items || []);
    } catch { /* oturum yok */ }
  }, []);

  const loadLinkSummary = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/games/link-summary');
      setLinkSummary(data);
      // Taslak yalnızca yönetici kaydedilmemiş bir seçim tutmuyorsa
      // tazelenir; aksi hâlde arka plandaki her yenileme seçimini silerdi.
      setLinkPolicyDraft((current) => (
        current && current.dirty ? current : {
          ...(data.link_policy || EMPTY_LINK_POLICY), dirty: false,
        }
      ));
    } catch { /* özet alınamadı */ }
  }, []);

  // Kaynak adı uçtan gelir (``link_sources``); panele sabit yazılsaydı yeni
  // bir sağlayıcı eklendiğinde seçicide kimliği görünürdü.
  const linkSources = linkSummary?.link_sources || [];
  const linkSourceLabel = (id) => (
    linkSources.find((s) => s.id === id)?.name || (id || 'otomatik seçim')
  );

  // Seçilen kaynak kaydedilir; uygulaması "Oto Eşleştir" düğmesindedir.
  // Kaydın kendisi kataloğu değiştirmez — sessizce tüm vitrini yeniden
  // planlamak, ayarı değiştiren yöneticiye haber vermeden siteyi değiştirmek
  // olurdu.
  const saveLinkPolicy = async () => {
    const draft = linkPolicyDraft || EMPTY_LINK_POLICY;
    setPolicyBusy(true);
    try {
      await client.post('/admin/games/link-policy', {
        reel_source: draft.reel_source || '',
        demo_source: draft.demo_source || '',
      });
      toast({
        title: 'Kaynak tercihi kaydedildi',
        description: 'Uygulamak için "Oto Eşleştir" düğmesine basın.',
      });
      setLinkPolicyDraft((current) => ({ ...(current || {}), dirty: false }));
      await loadLinkSummary();
    } catch (e) {
      toast({
        title: 'Tercih kaydedilemedi',
        description: e.response?.data?.detail || 'Hata oluştu',
      });
    } finally {
      setPolicyBusy(false);
    }
  };

  // "Oto Eşleştir": aynı oyunun farklı API'lerdeki kopyalarını birleştirir —
  // birini reel seçer, demo verebilen kopyayı demo kaynağı yapar, kalanları
  // gizler. Yönetici elle kilitlediği eşleşmeler korunur.
  // Anahtarlar önce tazelenir (rebuild_keys): eşleştirme anahtarı senkron
  // sırasında yazıldığı için üretici eşdeğerlikleri güncellendiğinde katalogdaki
  // eski anahtarlar aynı kalır ve o kopyalar birleşmezdi.
  const linkDuplicates = async () => {
    if (linkSummary?.can_auto_link === false) {
      toast({
        title: 'Oto eşleştirme kullanılamıyor',
        description: linkSummary.link_blocker || 'Seçilen servis kullanıma hazır değil.',
      });
      return;
    }
    // Kaydedilmemiş bir seçimle çalıştırmak, ekranda yazandan başka bir
    // kaynağı uygulardı: uç her zaman kayıtlı tercihi okur.
    if (linkPolicyDraft?.dirty) {
      toast({
        title: 'Önce kaynak tercihini kaydedin',
        description: 'Seçim kaydedilmeden eşleştirme eski tercihi uygular.',
      });
      return;
    }
    if (!window.confirm(
      'DİKKAT: Bu işlem TÜM kataloğu etkiler.\n\n'
      + `Aynı oyunun farklı API kopyaları birleştirilecek (${linkSummary?.total ?? '?'} oyun taranır). `
      + `Reel ${linkSourceLabel(linkSummary?.link_policy?.reel_source)}, `
      + `demo ${linkSourceLabel(linkSummary?.link_policy?.demo_source)} olur; `
      + 'kopyalar sitede gizlenir. Elle kilitlediğiniz eşleşmelere dokunulmaz.\n\n'
      + 'Devam edilsin mi?',
    )) return;
    setLinkBusy(true);
    try {
      const { data } = await client.post(
        '/admin/games/link-duplicates', null, { params: { rebuild_keys: true } },
      );
      toast({
        title: 'Kopyalar birleştirildi',
        description: `${data.groups} oyun eşleşti · ${data.demo_bound} demo bağlandı`
          + ` · ${data.hidden} kopya gizlendi`
          + (data.no_demo ? ` · ${data.no_demo} oyunun demosu yok` : ''),
      });
      await Promise.all([loadGames(), loadLinkSummary()]);
    } catch (e) {
      toast({ title: 'Birleştirilemedi', description: e.response?.data?.detail || 'Hata oluştu' });
    } finally {
      setLinkBusy(false);
    }
  };

  const loadTwins = useCallback(async (gid) => {
    setTwinsBusy(true);
    try {
      const { data } = await client.get(`/admin/games/${gid}/twins`);
      const rows = data.items || [];
      setTwins(rows);
      setTwinsKey(data.catalog_key || '');
      setTwinsSplit(Boolean(data.split));
      // Taslağın başlangıcı DB'deki mevcut durumdur; "değişti mi" karşılaştırması
      // buna göre yapılır.
      const merged = rows.some((t) => t.merged_into);
      const current = merged ? rows.find((t) => !t.merged_into) : null;
      const state = {
        primaryId: current?.id || null,
        demoGameId: current?.demo_game_id || null,
        unlink: false,
      };
      setTwinDraft(state);
      setTwinOriginal(state);
    } catch {
      setTwins([]);
      setTwinsKey('');
      setTwinsSplit(false);
      setTwinDraft(EMPTY_TWIN_DRAFT);
      setTwinOriginal(EMPTY_TWIN_DRAFT);
    } finally {
      setTwinsBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Görüntü taslaktan çizilir: yönetici "Reel yap" dedikten sonra rozetler
  // hemen yeni duruma göre görünür ama DB'ye henüz yazılmamıştır.
  const twinsPrimaryId = twinDraft.unlink ? null : twinDraft.primaryId;
  const twinsDirty = (
    twinDraft.unlink !== twinOriginal.unlink
    || (twinDraft.primaryId || null) !== (twinOriginal.primaryId || null)
    || (twinDraft.demoGameId || null) !== (twinOriginal.demoGameId || null)
  );

  // Bir kayıt reel seçilince demo kaynağı da onunla birlikte belirlenir:
  // kendisi demo verebiliyorsa kendisinden, veremiyorsa gruptaki demo verebilen
  // kopyadan. (Backend make-primary da aynı kuralı uygular.)
  const demoSourceFor = (row) => {
    if (!row) return null;
    if (row.can_serve_demo) return null;
    const donor = twins.find(
      (t) => t.id !== row.id && t.can_serve_demo && t.game_id);
    return donor?.id || null;
  };

  // Modal içi reel/demo seçimleri yalnızca taslağı değiştirir; DB'ye yazma
  // Kaydet'te (applyTwinDraft) tek seferde yapılır.
  const makeReel = (gid) => {
    const row = twins.find((t) => t.id === gid) || null;
    setTwinDraft({
      primaryId: gid,
      demoGameId: demoSourceFor(row),
      unlink: false,
    });
  };
  const setDemoSource = (demoGameId) => setTwinDraft((d) => ({
    ...d, demoGameId: demoGameId || null, unlink: false,
  }));
  const unlinkGame = () => setTwinDraft({
    primaryId: null, demoGameId: null, unlink: true,
  });
  const resetTwinDraft = () => setTwinDraft(twinOriginal);

  /** Taslak eşleştirmeyi uygular (Kaydet akışının parçası). */
  const applyTwinDraft = async () => {
    if (!twinsDirty) return;
    if (twinDraft.unlink) {
      const target = twinOriginal.primaryId || modal?.id;
      if (target) await client.post(`/admin/games/${target}/unlink`);
      return;
    }
    const primaryChanged = (
      twinDraft.primaryId && twinDraft.primaryId !== twinOriginal.primaryId);
    if (primaryChanged) {
      await client.post(`/admin/games/${twinDraft.primaryId}/make-primary`);
    }
    // make-primary demo kaynağını kendi kuralıyla yazar; taslak ondan farklıysa
    // (yönetici elle seçtiyse) ayrıca bildirilir.
    const target = twinDraft.primaryId || twinOriginal.primaryId;
    const demoChanged = (
      (twinDraft.demoGameId || null) !== (twinOriginal.demoGameId || null));
    if (target && demoChanged && !primaryChanged) {
      await client.post(`/admin/games/${target}/demo-link`, {
        demo_game_id: twinDraft.demoGameId || null,
      });
    } else if (target && primaryChanged) {
      const auto = demoSourceFor(twins.find((t) => t.id === target) || null);
      if ((twinDraft.demoGameId || null) !== (auto || null)) {
        await client.post(`/admin/games/${target}/demo-link`, {
          demo_game_id: twinDraft.demoGameId || null,
        });
      }
    }
  };

  const loadLabels = useCallback(async () => {
    try {
      const { data } = await client.get('/admin/provider-labels');
      setLabels(data.items || []);
      // Taslak, kaydedilmemiş düzenlemeleri tutar; kayıttan sonra sıfırlanır.
      setLabelDrafts({});
    } catch (requestError) {
      toast({
        title: 'Sağlayıcı görünümleri alınamadı',
        description: requestError.response?.data?.detail || 'Tekrar deneyin',
      });
    }
  }, [toast]);

  const saveLabel = async (slug) => {
    const current = labels.find((item) => item.slug === slug) || {};
    const draft = { ...current, ...(labelDrafts[slug] || {}) };
    setLabelBusy(slug);
    try {
      await client.patch(`/admin/provider-labels/${encodeURIComponent(slug)}`, {
        display_name: draft.name || '',
        logo: draft.logo || '',
        order: Number(draft.order) || 0,
        hidden: !!draft.hidden,
      });
      toast({ title: 'Kaydedildi', description: `${draft.name || slug} güncellendi.` });
      await loadLabels();
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setLabelBusy('');
    }
  };

  const uploadLabelLogo = async (slug, file) => {
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    setLabelBusy(slug);
    try {
      const { data } = await client.post('/admin/uploads/images', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLabelDrafts((current) => ({
        ...current,
        [slug]: { ...(current[slug] || {}), logo: data.url },
      }));
      toast({ title: 'Logo yüklendi', description: 'Kaydet ile onaylayın.' });
    } catch (requestError) {
      toast({
        title: 'Yüklenemedi',
        description: requestError.response?.data?.detail || 'Görsel yüklenemedi',
      });
    } finally {
      setLabelBusy('');
    }
  };

  useEffect(() => { loadGames(); }, [loadGames]);
  useEffect(() => {
    if (view === 'games') loadShowcase();
  }, [loadShowcase, view]);
  useEffect(() => {
    loadMeta();
    loadSessions();
    loadCategories();
    loadLinkSummary();
  }, [loadMeta, loadSessions, loadCategories, loadLinkSummary]);
  // Vitrin çipleri tüm katalogdan türetilir; yalnızca sekmesi açılınca gerekir.
  useEffect(() => { if (view === 'labels') loadLabels(); }, [view, loadLabels]);

  const changeViewMode = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('admin:games-view-mode', mode); } catch { /* yoksay */ }
  };

  const toggleCategoriesPanel = () => {
    setCategoriesOpen((open) => {
      const next = !open;
      try { localStorage.setItem('admin:games-cat-open', next ? '1' : '0'); } catch { /* yoksay */ }
      return next;
    });
  };

  const toggleLinkPanel = () => {
    setLinkPanelOpen((open) => {
      const next = !open;
      try { localStorage.setItem('admin:games-link-open', next ? '1' : '0'); } catch { /* yoksay */ }
      return next;
    });
  };

  const toggleShowcasePanel = () => {
    setShowcaseOpen((open) => {
      const next = !open;
      try { localStorage.setItem('admin:games-showcase-open', next ? '1' : '0'); } catch { /* yoksay */ }
      return next;
    });
  };

  // Anahtarsız kayıtlar eşleştirmeye hiç giremez. Anahtar üretici + oyun
  // adından türediği için sağlayıcıya gitmeden yeniden hesaplanır.
  const rebuildKeys = async () => {
    setKeysBusy(true);
    try {
      const { data } = await client.post('/admin/games/rebuild-keys');
      toast({
        title: 'Eşleştirme anahtarları üretildi',
        description: `${data.updated} oyuna anahtar yazıldı`
          + (data.skipped ? ` · ${data.skipped} kayıt adsız olduğu için atlandı` : ''),
      });
      await loadLinkSummary();
    } catch (e) {
      // Yanıt gövdesi yoksa (ters vekil zaman aşımı, ağ kopması) sebep
      // yalnızca e.message'da durur; "Hata oluştu" teşhis ettirmiyordu.
      toast({
        title: 'Anahtarlar üretilemedi',
        description: e.response?.data?.detail
          || (e.response ? `Sunucu ${e.response.status} döndü` : e.message)
          || 'Hata oluştu',
      });
    } finally {
      setKeysBusy(false);
    }
  };

  const toggleCategory = async (cid) => {
    try {
      const { data } = await client.post(`/admin/games/categories/${cid}/toggle`);
      toast({
        title: data.enabled ? 'Kategori açıldı' : 'Kategori kapatıldı',
        description: data.enabled
          ? 'Oyunlar sitede yeniden görünür'
          : 'Bu kategori sitede hiç görünmez ve oyunlar açılamaz',
      });
      loadCategories();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    }
  };

  const bulkCategory = async (cid, active) => {
    if (!window.confirm(`${cid} kategorisindeki TÜM oyunlar ${active ? 'aktif' : 'pasif'} yapılsın mı?`)) return;
    try {
      const { data } = await client.post(`/admin/games/categories/${cid}/bulk`, null, { params: { active } });
      toast({ title: 'Toplu güncellendi', description: `${data.updated} oyun güncellendi` });
      loadCategories(); loadGames();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    }
  };

  const bulkProvider = async (name, active) => {
    if (!window.confirm(`${name} sağlayıcısının TÜM oyunları ${active ? 'aktif' : 'pasif'} yapılsın mı?`)) return;
    try {
      const { data } = await client.post(`/admin/games/providers/${encodeURIComponent(name)}/bulk`,
        null, { params: { active } });
      toast({ title: 'Toplu güncellendi', description: `${data.updated} oyun güncellendi` });
      loadCategories(); loadGames();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'İşlem başarısız' });
    }
  };

  const syncGames = async (providerId) => {
    setSyncing(providerId ? `gold-games-${providerId}` : 'gold-games');
    try {
      const { data } = await client.post('/admin/games/goldpalace-sync', null, {
        params: providerId ? { provider_id: providerId } : {},
      });
      toast({
        title: 'Oyunlar senkronize edildi',
        description: `${data.created} yeni, ${data.updated} güncellendi${data.failed_providers ? ` · ${data.failed_providers} sağlayıcı hatalı` : ''}`,
      });
      if (data.errors?.length) {
        toast({ title: 'Bazı sağlayıcılar alınamadı', description: data.errors.map((x) => x.provider).join(', ') });
      }
      await loadGames();
    } catch (e) {
      toast({ title: 'Senkronizasyon başarısız', description: e.response?.data?.detail || 'Hata oluştu' });
    } finally { setSyncing(''); }
  };

  const syncDrakon = async (what) => {
    setSyncing(`drakon-${what}`);
    try {
      const path = what === 'providers' ? '/admin/drakon/sync-providers' : '/admin/drakon/sync';
      const { data } = await client.post(path);
      toast({
        title: 'Drakon senkronize edildi',
        description: `${data.created} yeni, ${data.updated} güncellendi`,
      });
      await loadMeta();
      if (what === 'games') await loadGames();
    } catch (e) {
      toast({ title: 'Drakon senkronizasyonu başarısız', description: e.response?.data?.detail || 'Hata oluştu' });
    } finally { setSyncing(''); }
  };

  const syncOroplay = async (vendorCode) => {
    setSyncing(vendorCode ? `oroplay-games-${vendorCode}` : 'oroplay-games');
    try {
      const { data } = await client.post('/admin/games/oroplay-sync', null, {
        params: vendorCode ? { vendor_code: vendorCode } : {},
      });
      toast({
        title: 'OroPlay senkronize edildi',
        description: `${data.created} yeni, ${data.updated} güncellendi${data.failed_providers ? ` · ${data.failed_providers} sağlayıcı hatalı` : ''}`,
      });
      if (data.errors?.length) {
        toast({
          title: 'Bazı sağlayıcılar alınamadı',
          description: data.errors.map((x) => x.provider || x.vendor_code).join(', '),
        });
      }
      await loadGames();
    } catch (e) {
      toast({ title: 'Senkronizasyon başarısız', description: e.response?.data?.detail || 'Hata oluştu' });
    } finally { setSyncing(''); }
  };

  const syncProviderGames = (provider) => {
    if (provider.api_provider === 'goldpalace') {
      return syncGames(provider.provider_id);
    }
    if (provider.api_provider === 'oroplay') {
      return syncOroplay(provider.provider_code);
    }
    if (provider.api_provider === 'drakon') {
      return syncDrakon('games');
    }
    toast({
      title: 'Eski servis kapalı',
      description: 'ScorpioPlay oyunları pasif tutuluyor ve yeniden senkronize edilmiyor',
    });
    return null;
  };

  const openEdit = (g) => {
    setForm({
      ...empty,
      ...g,
      api_image: g.api_image || '',
      image_override: g.image_override || '',
      provider_id: g.provider_id ?? '',
      game_id: g.game_id ?? '',
    });
    setModal({ id: g.id, originalImageOverride: g.image_override || '' });
    setModalTab('settings');
    setTwins([]);
    setTwinDraft(EMPTY_TWIN_DRAFT);
    setTwinOriginal(EMPTY_TWIN_DRAFT);
    loadTwins(g.id);
  };

  const uploadGameImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }

    setUploadingImage(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/games/image', body);
      setForm((current) => ({ ...current, image_override: data.url }));
      toast({ title: 'Özel oyun görseli yüklendi' });
    } catch (error) {
      toast({
        title: 'Görsel yüklenemedi',
        description: error.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        category: form.category,
        active: form.active,
        featured: form.featured,
        hot: form.hot,
        new: form.new,
        order: Number(form.order),
      };
      if ((form.image_override || '') !== modal.originalImageOverride) {
        payload.image = form.image_override || '';
      }
      await client.patch(`/admin/games/${modal.id}`, payload);
      // Reel/demo seçimi de aynı Kaydet ile uygulanır.
      await applyTwinDraft();
      toast({
        title: 'Kaydedildi',
        description: twinsDirty ? 'Reel/demo eşleştirmesi de güncellendi' : undefined,
      });
      setModal(null);
      await Promise.all([loadGames(), loadShowcase(), loadLinkSummary()]);
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Başarısız' });
    } finally { setBusy(false); }
  };

  const toggle = async (g, k) => {
    const previous = items;
    setItems((current) => current.map((item) => (
      item.id === g.id ? { ...item, [k]: !item[k] } : item
    )));
    try {
      const { data } = await client.patch(`/admin/games/${g.id}`, { [k]: !g[k] });
      // Liste yeniden yüklenmez: her açma/kapamada yükleme ekranına düşüp
      // sayfanın başına dönmek, oyunları tek tek düzenleyen yöneticiyi yerini
      // kaybettiriyordu. Sunucunun döndürdüğü satır yerine yazılır; yalnızca
      // süzgeç bu alana bakıyorsa (ör. "Öne Çıkan") liste tazelenir, çünkü
      // oyun artık o listeye ait olmayabilir.
      const reloads = [];
      if (data?.id) {
        setItems((current) => current.map((item) => (
          item.id === data.id ? { ...item, ...data } : item
        )));
      }
      if (statusFilter !== 'all' || !data?.id) reloads.push(loadGames());
      if (['featured', 'hot', 'new'].includes(k)) reloads.push(loadShowcase());
      await Promise.all(reloads);
    } catch (requestError) {
      setItems(previous);
      toast({
        title: 'Güncellenemedi',
        description: requestError.response?.data?.detail || 'Değişiklik geri alındı',
      });
    }
  };

  const moveShowcase = (game, direction) => {
    const index = showcaseGames.findIndex((item) => item.id === game.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= showcaseGames.length) return;
    const reordered = [...showcaseGames];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    persistGameOrder(reordered, items);
  };

  const toggleProvider = async (p) => {
    await client.patch(`/admin/games/providers/${p.id}`, { active: !p.active });
    loadMeta();
  };

  const persistProviderOrder = async (reordered, previous) => {
    const normalized = reordered.map((providerItem, index) => ({
      ...providerItem,
      order: index + 1,
    }));
    setProviders(normalized);
    setProviderOrderBusy(true);
    try {
      await client.patch('/admin/games/providers/reorder', {
        ordered_ids: normalized.map((providerItem) => providerItem.id),
      });
    } catch (requestError) {
      setProviders(previous);
      toast({
        title: 'Sağlayıcı sırası kaydedilemedi',
        description: requestError.response?.data?.detail || 'Önceki sıra geri yüklendi',
      });
    } finally {
      setProviderOrderBusy(false);
    }
  };

  const moveProvider = (providerItem, direction) => {
    if (providerOrderBusy) return;
    const index = providers.findIndex((item) => item.id === providerItem.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= providers.length) return;
    const reordered = [...providers];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    persistProviderOrder(reordered, providers);
  };

  const setProviderOrder = (providerItem, requestedOrder) => {
    if (providerOrderBusy) return;
    const index = providers.findIndex((item) => item.id === providerItem.id);
    if (index < 0) return;
    const target = Math.max(0, Math.min(providers.length - 1, requestedOrder - 1));
    if (target === index) return;
    const reordered = [...providers];
    reordered.splice(index, 1);
    reordered.splice(target, 0, providerItem);
    persistProviderOrder(reordered, providers);
  };

  const closeSession = async (s) => {
    if (!window.confirm(`${s.username} oturumu kapatılsın ve bakiye geri alınsın mı?`)) return;
    try {
      const { data } = await client.post(`/admin/games/sessions/${s.id}/close`);
      toast({ title: 'Oturum kapatıldı', description: `${money(data.returned)} bakiyeye aktarıldı` });
      loadSessions();
    } catch (e) {
      toast({ title: 'Hata', description: e.response?.data?.detail || 'Kapatılamadı' });
    }
  };

  // Arama ve filtreler sunucuda tüm katalog üzerinde uygulanır; bu sayede
  // yalnızca açık sayfadaki 100 oyunu süzme hatası oluşmaz.
  const filtered = items;
  const showcase = showcaseTabs.find((tab) => tab.id === showcaseTab) || showcaseTabs[0];
  // Vitrin sırası site sırasıyla aynı alandır (games.order); şerit o sırayla dolar.
  const showcaseGames = [...(showcaseItems[showcase.flag] || [])]
    .sort(compareGameOrder);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Özet kartları hâlihazırda yüklü olan iki kaynaktan türer (kategori özeti +
  // eşleştirme özeti); sayfa bunun için fazladan istek atmaz.
  const catalogTotals = categories.reduce((sum, row) => ({
    total: sum.total + Number(row.total || 0),
    active: sum.active + Number(row.active || 0),
    inactive: sum.inactive + Number(row.inactive || 0),
  }), { total: 0, active: 0, inactive: 0 });
  const closedCategories = categories.filter((row) => !row.enabled);

  // Uygulanan süzgeçler tek bir şeritte rozet olarak görünür: hangi filtrenin
  // açık olduğu üç ayrı satıra bakmadan anlaşılmalı, her rozet kendi
  // filtresini kaldırabilmelidir.
  const providerFilterLabel = () => {
    if (provFilter === 'all') return '';
    const [apiProvider, rawId] = provFilter.split(':', 2);
    const match = providers.find((p) => (
      String(p.provider_id) === rawId && (p.api_provider || '') === apiProvider));
    return match?.name || provFilter;
  };
  const statusLabels = {
    featured: 'Öne Çıkan', hot: 'Popüler', new: 'Yeni',
    active: 'Aktif', inactive: 'Devre Dışı',
  };
  const activeFilters = [
    searchTerm && { key: 'q', label: `Arama: “${searchTerm}”`, clear: () => { setQ(''); setPage(0); } },
    provFilter !== 'all' && {
      key: 'prov',
      label: `Sağlayıcı: ${providerFilterLabel()}`,
      clear: () => { setProvFilter('all'); setPage(0); },
    },
    statusFilter !== 'all' && {
      key: 'status',
      label: `Durum: ${statusLabels[statusFilter] || statusFilter}`,
      clear: () => { setStatusFilter('all'); setPage(0); },
    },
    demoState !== 'all' && {
      key: 'demo',
      label: demoState === 'has' ? 'Demosu var' : 'Demosu yok',
      clear: () => { setDemoState('all'); setPage(0); },
    },
    cat === 'live-casino' && liveGameType !== 'all' && {
      key: 'live',
      label: liveGameTypes.find((t) => t.id === liveGameType)?.name || liveGameType,
      clear: () => { setLiveGameType('all'); setPage(0); },
    },
    showMerged && {
      key: 'merged',
      label: 'Gizli demo kaynakları dahil',
      clear: () => { setShowMerged(false); setPage(0); },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setQ('');
    setProvFilter('all');
    setStatusFilter('all');
    setDemoState('all');
    setLiveGameType('all');
    setShowMerged(false);
    setPage(0);
  };

  const persistGameOrder = async (reordered, previous) => {
    setGameOrderBusy(true);
    try {
      const { data } = await client.patch('/admin/games/reorder', {
        category: cat,
        ordered_ids: reordered.map((item) => item.id),
      });
      const positionById = new Map(
        (data.ordered_ids || []).map((gameId, index) => [gameId, index + 1]),
      );
      setItems((current) => [...current]
        .map((item) => ({
          ...item,
          order: positionById.get(item.id) ?? item.order,
        }))
        .sort(compareGameOrder));
      setShowcaseItems((current) => Object.fromEntries(
        Object.entries(current).map(([flag, games]) => [
          flag,
          [...games]
            .map((item) => ({
              ...item,
              order: positionById.get(item.id) ?? item.order,
            }))
            .sort(compareGameOrder),
        ]),
      ));
    } catch (requestError) {
      setItems(previous);
      toast({
        title: 'Oyun sırası kaydedilemedi',
        description: requestError.response?.data?.detail || 'Önceki sıra geri yüklendi',
      });
    } finally {
      setGameOrderBusy(false);
    }
  };

  // Görseli panelden konmuş oyunlar tek istekte kategorinin başına alınır;
  // tek tek yukarı taşımak her adımda bir istek ve bir liste yenilemesiydi.
  const promoteCustomImages = async () => {
    const catName = cats.find((c) => c.id === cat)?.name || cat;
    if (!window.confirm(
      `${catName} kategorisinde görseli eklenmiş oyunlar listenin en üstüne alınsın mı? `
      + 'Kendi aralarındaki sıra korunur.',
    )) return;
    setGameOrderBusy(true);
    try {
      const { data } = await client.post('/admin/games/reorder/custom-images-first', {
        category: cat,
      });
      toast({
        title: 'Sıra güncellendi',
        description: data.promoted
          ? `${data.promoted} görselli oyun en üste alındı`
          : 'Bu kategoride görseli eklenmiş oyun yok',
      });
      await Promise.all([loadGames(), loadShowcase()]);
    } catch (requestError) {
      toast({
        title: 'Sıra güncellenemedi',
        description: requestError.response?.data?.detail || 'Başarısız',
      });
    } finally {
      setGameOrderBusy(false);
    }
  };

  const moveGame = (game, direction) => {
    if (gameOrderBusy) return;
    const index = filtered.findIndex((item) => item.id === game.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= filtered.length) return;
    const reordered = [...filtered];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    persistGameOrder(reordered, items);
  };

  const setGameOrder = (game, requestedOrder) => {
    if (gameOrderBusy) return;
    const index = filtered.findIndex((item) => item.id === game.id);
    if (index < 0) return;
    const target = Math.max(0, Math.min(filtered.length - 1, requestedOrder - 1));
    if (target === index) return;
    const reordered = [...filtered];
    reordered.splice(index, 1);
    reordered.splice(target, 0, game);
    persistGameOrder(reordered, items);
  };

  const setShowcaseOrder = (game, requestedPosition) => {
    const reordered = [...showcaseGames];
    const currentIndex = reordered.findIndex((item) => item.id === game.id);
    const nextIndex = Math.max(
      0,
      Math.min(reordered.length - 1, Number(requestedPosition || 1) - 1),
    );
    if (currentIndex < 0 || currentIndex === nextIndex) return;
    const [moved] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, moved);
    persistGameOrder(reordered, items);
  };

  // Kart ve satır görünümü aynı veriyi çizer; ikisinde de aynı işlemler
  // bulunur, yalnızca yoğunluk değişir.
  const flagButtons = (g) => (
    <div className="gm-flags" role="group" aria-label={`${g.name} vitrin etiketleri`}>
      <button
        type="button"
        onClick={() => toggle(g, 'featured')}
        className={`gm-flag ${g.featured ? 'is-on featured' : ''}`}
        aria-pressed={g.featured}
        aria-label={`${g.name} — Öne çıkanlar şeridi`}
        title="Öne çıkanlar şeridine ekle / çıkar"
      >
        <Star size={13} fill={g.featured ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        onClick={() => toggle(g, 'hot')}
        className={`gm-flag ${g.hot ? 'is-on hot' : ''}`}
        aria-pressed={g.hot}
        aria-label={`${g.name} — Popüler şeridi`}
        title="Popüler şeridine ekle / çıkar"
      >
        <Flame size={13} fill={g.hot ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        onClick={() => toggle(g, 'new')}
        className={`gm-flag ${g.new ? 'is-on new' : ''}`}
        aria-pressed={g.new}
        aria-label={`${g.name} — Yeni eklenenler şeridi`}
        title="Yeni Eklenenler şeridine ekle / çıkar"
      >
        <Sparkles size={13} fill={g.new ? 'currentColor' : 'none'} />
      </button>
    </div>
  );

  const orderControls = (g, index) => (
    <div className="gm-order" role="group" aria-label={`${g.name} sıra denetimi`}>
      <label>
        <span className="sr-only">{g.name} site sıra numarası</span>
        <input
          key={`${g.id}-${index}`}
          type="number"
          min="1"
          max={filtered.length}
          defaultValue={index + 1}
          disabled={gameOrderBusy}
          onBlur={(event) => {
            const currentPosition = index + 1;
            const requested = Math.max(
              1,
              Math.min(filtered.length, Number(event.currentTarget.value) || currentPosition),
            );
            event.currentTarget.value = String(requested);
            setGameOrder(g, requested);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
        />
      </label>
      <button
        type="button"
        onClick={() => moveGame(g, -1)}
        disabled={gameOrderBusy || index === 0}
        aria-label={`${g.name} oyununu yukarı taşı`}
        title="Yukarı taşı"
      >
        <ArrowUp size={13} />
      </button>
      <button
        type="button"
        onClick={() => moveGame(g, 1)}
        disabled={gameOrderBusy || index === filtered.length - 1}
        aria-label={`${g.name} oyununu aşağı taşı`}
        title="Aşağı taşı"
      >
        <ArrowDown size={13} />
      </button>
    </div>
  );

  const sourceBadges = (g) => (
    <div className="gm-src">
      <span className="gm-src-tag reel" title="Reel oyun bu API'den açılır">
        <em>Reel</em> {g.reel_provider_name || g.api_provider || '—'}
      </span>
      {g.demo_available ? (
        <span className="gm-src-tag demo" title="Demo bu API'den açılır">
          <em>Demo</em> {g.demo_provider_name}
        </span>
      ) : (
        <span
          className="gm-src-tag off"
          title={g.demo_blocked
            ? `${g.demo_note}; 24 saat sonra yeniden denenir`
            : `Demo yok: ${g.demo_note}`}
        >
          <em>Demo</em> {g.demo_blocked ? 'reddedildi' : 'pasif'}
        </span>
      )}
      {g.merged_into && (
        <span className="gm-src-tag merged"
              title="Bu kayıt başka bir oyunun demo kaynağı; sitede görünmez">
          <EyeOff size={11} /> Gizli kaynak
        </span>
      )}
    </div>
  );

  return (
    <div className="gm-page">
      <header className="gm-hero">
        <span className="gm-hero-mark"><Gamepad2 size={23} /></span>
        <div className="gm-hero-copy">
          <h1>Oyun Yönetimi</h1>
          <p>Katalog, kategoriler, ana sayfa vitrini ve açık oyun oturumları.</p>
        </div>
        {/* Sağlayıcı bağlantısı, servis aç/kapa ve katalog senkronizasyonu tek
            yerdedir: Casino → Oyun Servisleri. */}
        <Link to="/admin/oyun-servisleri" className="gm-hero-link">
          <Server size={15} /> Oyun Servisleri <ArrowRight size={14} />
        </Link>
      </header>

      {/* Özet: kategori ve eşleştirme özetlerinden türer, ek istek atmaz. */}
      <div className="gm-stats">
        <div className="gm-stat">
          <span className="gm-stat-icon neutral"><Boxes size={17} /></span>
          <div>
            <strong>{catalogTotals.total.toLocaleString('tr-TR')}</strong>
            <span>Katalogdaki oyun</span>
          </div>
        </div>
        <div className="gm-stat">
          <span className="gm-stat-icon green"><CircleCheck size={17} /></span>
          <div>
            <strong>{catalogTotals.active.toLocaleString('tr-TR')}</strong>
            <span>Sitede açık</span>
          </div>
        </div>
        <div className="gm-stat">
          <span className="gm-stat-icon red"><CircleSlash size={17} /></span>
          <div>
            <strong>{catalogTotals.inactive.toLocaleString('tr-TR')}</strong>
            <span>Devre dışı</span>
          </div>
        </div>
        <div className="gm-stat">
          <span className="gm-stat-icon blue"><Link2 size={17} /></span>
          <div>
            <strong>{Number(linkSummary?.demo_bound || 0).toLocaleString('tr-TR')}</strong>
            <span>Demo kaynağı bağlı</span>
          </div>
        </div>
        <div className="gm-stat">
          <span className="gm-stat-icon muted"><EyeOff size={17} /></span>
          <div>
            <strong>{Number(linkSummary?.merged_hidden || 0).toLocaleString('tr-TR')}</strong>
            <span>Gizlenen kopya</span>
          </div>
        </div>
        {linkSummary?.unkeyed > 0 && (
          <div className="gm-stat is-warn">
            <span className="gm-stat-icon amber"><KeyRound size={17} /></span>
            <div>
              <strong>{Number(linkSummary.unkeyed).toLocaleString('tr-TR')}</strong>
              <span>Eşleştirme anahtarı yok</span>
            </div>
          </div>
        )}
      </div>

      {/* Bölüm sekmeleri */}
      <nav className="gm-viewbar" aria-label="Oyun yönetimi bölümleri">
        <button type="button" onClick={() => setView('games')}
                className={`gm-view ${view === 'games' ? 'is-active' : ''}`}
                aria-pressed={view === 'games'}>
          <Gamepad2 size={15} /> Oyunlar
        </button>
        <button type="button" onClick={() => setView('providers')}
                className={`gm-view ${view === 'providers' ? 'is-active' : ''}`}
                aria-pressed={view === 'providers'}>
          <Server size={15} /> Sağlayıcılar
          <span className="gm-view-count">{providers.length}</span>
        </button>
        <button type="button" onClick={() => setView('labels')}
                className={`gm-view ${view === 'labels' ? 'is-active' : ''}`}
                aria-pressed={view === 'labels'}>
          <ImageIcon size={15} /> Vitrin Çipleri
        </button>
        <button type="button" onClick={() => setView('sessions')}
                className={`gm-view ${view === 'sessions' ? 'is-active' : ''}`}
                aria-pressed={view === 'sessions'}>
          <Radio size={15} /> Aktif Oturumlar
          <span className="gm-view-count">{sessions.length}</span>
        </button>
      </nav>

      {view === 'games' && (
        <>
          {/* Yardımcı paneller yan yana durur ve kapalı başlar: ikisi de
              seyrek kullanılır, kapalıyken özet satırı yine bilgi taşır. */}
          <div className="gm-tools">
            <section className="gm-panel">
              <button type="button" className="gm-panel-head" onClick={toggleCategoriesPanel}
                      aria-expanded={categoriesOpen}>
                <span className="gm-panel-icon"><Layers size={15} /></span>
                <span className="gm-panel-title">
                  Kategori Kontrolü
                  <em>
                    {closedCategories.length > 0
                      ? `${closedCategories.length} kategori kapalı`
                      : 'Tüm kategoriler açık'}
                  </em>
                </span>
                <ChevronDown size={17}
                             className={`gm-panel-chevron ${categoriesOpen ? 'open' : ''}`} />
              </button>
              {categoriesOpen && (
                <div className="gm-panel-body">
                  <p className="gm-note">
                    Kapatılan kategori sitede hiç görünmez ve oyunları açılamaz.
                  </p>
                  <div className="gm-cat-grid">
                    {categories.map((row) => (
                      <div key={row.id} className={`gm-cat ${row.enabled ? '' : 'is-off'}`}>
                        <div className="gm-cat-head">
                          <span className="gm-cat-name">{row.name}</span>
                          <span className={`badge-pill ${row.enabled ? 'badge-green' : 'badge-red'}`}>
                            {row.enabled ? 'Açık' : 'Kapalı'}
                          </span>
                        </div>
                        <div className="gm-cat-stat">
                          <strong>{row.active}</strong> aktif / {row.total} oyun
                          {row.inactive > 0 && <span> · {row.inactive} pasif</span>}
                        </div>
                        <div className="gm-cat-actions">
                          <button type="button" onClick={() => toggleCategory(row.id)}
                                  className={row.enabled ? 'admin-btn-secondary text-xs' : 'admin-btn-primary text-xs'}>
                            {row.enabled ? <><PowerOff size={12} className="inline mr-1" />Kapat</>
                                         : <><Power size={12} className="inline mr-1" />Aç</>}
                          </button>
                          <button type="button" onClick={() => bulkCategory(row.id, true)}
                                  className="admin-btn-secondary text-xs"
                                  title="Kategorideki tüm oyunları aktif yap">Tümünü Aktif</button>
                          <button type="button" onClick={() => bulkCategory(row.id, false)}
                                  className="admin-btn-secondary text-xs"
                                  title="Kategorideki tüm oyunları pasif yap">Tümünü Pasif</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Reel/demo eşleştirmesi: çift kayıtları birleştirme ve durum özeti.
                Toplu işlem tehlikeli olduğu için panel kapalıyken buton gizlidir. */}
            <section className="gm-panel">
              <button type="button" className="gm-panel-head" onClick={toggleLinkPanel}
                      aria-expanded={linkPanelOpen}>
                <span className="gm-panel-icon"><Link2 size={15} /></span>
                <span className="gm-panel-title">
                  Reel / Demo Eşleştirmesi
                  <em>
                    {linkSummary
                      ? `${linkSummary.demo_bound} demo bağlı · ${linkSummary.merged_hidden} gizli kopya`
                      : 'Özet yükleniyor…'}
                  </em>
                </span>
                {linkSummary?.unkeyed > 0 && (
                  <span className="badge-pill badge-red gm-panel-alert"
                        title="Bu kayıtların eşleştirme anahtarı henüz yok">
                    {linkSummary.unkeyed} anahtarsız
                  </span>
                )}
                <ChevronDown size={17}
                             className={`gm-panel-chevron ${linkPanelOpen ? 'open' : ''}`} />
              </button>
              {linkPanelOpen && (
                <div className="gm-panel-body">
                  <p className="gm-note">
                    Aynı oyun birden çok API'de varsa reeli bir servisten, demosu başka bir
                    servisten açılır — sitede tek kayıt görünür. İki kaynağı aşağıdan siz
                    seçersiniz; boş bırakılan taraf sağlayıcı önceliğine düşer. Düğme önce
                    eşleştirme anahtarlarını tazeler (üretici adı iki API'de farklı yazılabiliyor:
                    "EGT" ↔ "egtdigital"), sonra tüm kopyaları birleştirir. İşlem{' '}
                    <strong>tüm kataloğu</strong> etkiler; onay ister.
                  </p>

                  {/* Reel ve demo kaynağı ayrı seçilir: eşleştirme bir süre
                      "reel Gold Palace, demo Drakon" olarak koda gömülüydü ve
                      o servisleri kullanmayan kurulumda seçenek yoktu. */}
                  <div className="gm-linkpolicy">
                    {[
                      {
                        role: 'reel_source',
                        title: 'Reel kaynağı',
                        hint: 'Sitede görünen, gerçek parayla açılan kayıt buradan seçilir.',
                        options: linkSources,
                      },
                      {
                        role: 'demo_source',
                        title: 'Demo kaynağı',
                        hint: 'Demo turu bu servisten açılır; demo veremeyen servisler listede yoktur.',
                        options: linkSources.filter((s) => s.supports_demo),
                      },
                    ].map(({
                      role, title, hint, options,
                    }) => {
                      const value = (linkPolicyDraft || EMPTY_LINK_POLICY)[role] || '';
                      // Seçili kaynak listeden düşmüşse (servis kaldırıldı)
                      // seçici boş görünürdü; kimliği olduğu gibi yazıp
                      // yöneticinin neyin kayıtlı olduğunu görmesini sağla.
                      const orphan = value && !options.some((s) => s.id === value);
                      return (
                        <label className="gm-linkpolicy-col" key={role}>
                          <span className="gm-linkpolicy-title">{title}</span>
                          <select
                            className="admin-input"
                            value={value}
                            onChange={(e) => setLinkPolicyDraft((current) => ({
                              ...(current || EMPTY_LINK_POLICY),
                              [role]: e.target.value,
                              dirty: true,
                            }))}
                          >
                            <option value="">{AUTO_SOURCE_LABEL}</option>
                            {options.map((source) => (
                              <option key={source.id} value={source.id}>
                                {source.name}
                                {source.enabled ? '' : ' — servis kapalı'}
                                {source.enabled && !source.configured ? ' — API bilgisi eksik' : ''}
                              </option>
                            ))}
                            {orphan && <option value={value}>{value} (tanınmıyor)</option>}
                          </select>
                          <span className="gm-linkpolicy-hint">{hint}</span>
                        </label>
                      );
                    })}
                    <button
                      type="button"
                      onClick={saveLinkPolicy}
                      disabled={policyBusy || !linkPolicyDraft?.dirty}
                      className="admin-btn-primary text-xs inline-flex items-center gap-2 whitespace-nowrap"
                      title="Seçimi kaydeder; uygulamak için Oto Eşleştir'e basın"
                    >
                      {policyBusy
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Link2 size={13} />}
                      Kaynağı Kaydet
                    </button>
                  </div>
                  {linkPolicyDraft?.dirty && (
                    <p className="gm-note gm-linkpolicy-dirty">
                      Seçim kaydedilmedi — <strong>Kaynağı Kaydet</strong>'e basmadan yapılan
                      eşleştirme eski tercihi uygular.
                    </p>
                  )}
                  {linkSummary && (
                    <div className="gm-linkstats">
                      <span><b>{linkSummary.total}</b> oyun tarandı</span>
                      <span><b>{linkSummary.demo_bound}</b> demo bağlı</span>
                      <span><b>{linkSummary.merged_hidden}</b> gizli kopya</span>
                      {linkSummary.locked > 0 && <span className="ok"><b>{linkSummary.locked}</b> elle kilitli</span>}
                    </div>
                  )}
                  {/* Anahtarsız kayıt varsa eşleştirme onları hiç göremez;
                      önce anahtar üretilmeli. Uyarı ve düzeltme butonu birlikte. */}
                  {linkSummary?.unkeyed > 0 && (
                    <div className="gm-alert">
                      <div>
                        <strong>{linkSummary.unkeyed} oyunun eşleştirme anahtarı yok.</strong>{' '}
                        Bu kayıtlar birleştirmeye hiç girmez. Anahtar, üretici ve oyun adından
                        üretilir — sağlayıcıya istek atılmaz.
                      </div>
                      <button
                        type="button"
                        onClick={rebuildKeys}
                        disabled={keysBusy}
                        className="admin-btn-primary text-xs inline-flex items-center gap-2 whitespace-nowrap"
                        title="Eksik catalog_key değerlerini mevcut kayıtlardan üret"
                      >
                        {keysBusy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />}
                        Anahtarları Üret
                      </button>
                    </div>
                  )}
                  {linkSummary?.can_auto_link === false && (
                    <div className="gm-alert">
                      <div>
                        <strong>Oto eşleştirme kapalı.</strong> {linkSummary.link_blocker}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={linkDuplicates}
                    disabled={linkBusy || linkSummary?.can_auto_link === false}
                    className="admin-btn-secondary text-xs inline-flex items-center gap-2"
                    title={linkSummary?.link_blocker
                      || 'Anahtarları tazeler, aynı oyunun farklı API kopyalarını birleştirir'}
                  >
                    {linkBusy ? <Loader2 size={13} className="animate-spin" /> : <Layers size={13} />}
                    {/* Etiket kayıtlı tercihi yazar, taslağı değil: düğme her
                        zaman sunucudaki seçimi uygular. */}
                    Oto Eşleştir (Reel {linkSourceLabel(linkSummary?.link_policy?.reel_source)}
                    {' · '}Demo {linkSourceLabel(linkSummary?.link_policy?.demo_source)})
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* Süzgeçler tek kartta toplanır: arama, kategori, durum ve demo
              ölçütleri ayrı satırlara dağılınca hangi filtrenin açık olduğu
              görünmüyordu. */}
          <section className="gm-filters" aria-label="Katalog süzgeçleri">
            <div className="gm-filters-row">
              <div className="gm-seg" role="group" aria-label="Oyun kategorisi">
                {cats.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => {
                      setCat(c.id);
                      setLiveGameType('all');
                      setPage(0);
                    }}
                    className={`gm-seg-btn ${cat === c.id ? 'is-active' : ''}`}
                    aria-pressed={cat === c.id}
                  >
                    {c.name}
                  </button>
                ))}
              </div>

              <form
                className="gm-search"
                role="search"
                onSubmit={(event) => {
                  event.preventDefault();
                  setSearchTerm(q.trim());
                  setPage(0);
                }}
              >
                <label className="sr-only" htmlFor="admin-game-search">Oyun ara</label>
                <Search size={16} className="gm-search-icon" aria-hidden="true" />
                <input
                  id="admin-game-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="gm-search-input"
                  placeholder="Oyun ya da sağlayıcı ara…"
                />
                {q && (
                  <button
                    type="button"
                    className="gm-search-clear"
                    onClick={() => setQ('')}
                    aria-label="Aramayı temizle"
                  >
                    <X size={14} />
                  </button>
                )}
              </form>

              <button
                type="button"
                className="admin-btn-secondary gm-promote"
                onClick={promoteCustomImages}
                disabled={gameOrderBusy}
                title="Görseli panelden eklenmiş oyunları bu kategorinin en üstüne alır"
              >
                <ArrowUpToLine size={14} /> Görselli oyunları öne al
              </button>

              <div className="gm-modes" role="group" aria-label="Katalog görünümü">
                <button
                  type="button"
                  onClick={() => changeViewMode('grid')}
                  className={`gm-mode ${viewMode === 'grid' ? 'is-active' : ''}`}
                  aria-pressed={viewMode === 'grid'}
                  title="Kart görünümü"
                >
                  <LayoutGrid size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => changeViewMode('list')}
                  className={`gm-mode ${viewMode === 'list' ? 'is-active' : ''}`}
                  aria-pressed={viewMode === 'list'}
                  title="Satır görünümü"
                >
                  <Rows3 size={15} />
                </button>
              </div>
            </div>

            <div className="gm-filters-row">
              <div className="gm-chips" role="group" aria-label="Durum süzgeci">
                {[
                  ['all', 'Tümü', Gamepad2],
                  ['featured', 'Öne Çıkan', Star],
                  ['hot', 'Popüler', Flame],
                  ['new', 'Yeni', Sparkles],
                  ['active', 'Aktif', Power],
                  ['inactive', 'Devre Dışı', PowerOff],
                ].map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setStatusFilter(id);
                      setPage(0);
                    }}
                    className={`gm-chip ${statusFilter === id ? 'is-active' : ''}`}
                    aria-pressed={statusFilter === id}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>

              <div className="gm-selects">
                {cat === 'live-casino' && (
                  <select
                    value={liveGameType}
                    onChange={(event) => {
                      setLiveGameType(event.target.value);
                      setPage(0);
                    }}
                    className="admin-input gm-select"
                    aria-label="Canlı casino oyun türü"
                  >
                    {liveGameTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                )}
                <select
                  value={provFilter}
                  onChange={(event) => {
                    setProvFilter(event.target.value);
                    setPage(0);
                  }}
                  className="admin-input gm-select"
                  aria-label="Sağlayıcı süzgeci"
                >
                  <option value="all">Tüm Sağlayıcılar</option>
                  {providers.map((p) => (
                    <option key={p.id} value={`${p.api_provider || ''}:${p.provider_id}`}>
                      {p.name} · {p.api_provider || 'manuel'}
                    </option>
                  ))}
                </select>
                <select
                  value={demoState}
                  onChange={(event) => { setDemoState(event.target.value); setPage(0); }}
                  className="admin-input gm-select"
                  aria-label="Demo durumu süzgeci"
                >
                  <option value="all">Demo: Tümü</option>
                  <option value="has">Demosu Var</option>
                  <option value="missing">Demosu Yok</option>
                </select>
                <label className="gm-check" title="Başka bir kayda demo kaynağı olarak bağlanmış, sitede gizli kopyalar">
                  <input
                    type="checkbox"
                    checked={showMerged}
                    onChange={(e) => { setShowMerged(e.target.checked); setPage(0); }}
                  />
                  Gizli kaynaklar
                </label>
              </div>
            </div>

            {activeFilters.length > 0 && (
              <div className="gm-applied">
                <SlidersHorizontal size={13} aria-hidden="true" />
                {activeFilters.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="gm-applied-chip"
                    onClick={item.clear}
                    title="Bu süzgeci kaldır"
                  >
                    {item.label} <X size={11} />
                  </button>
                ))}
                <button type="button" className="gm-applied-clear" onClick={clearFilters}>
                  <RotateCcw size={12} /> Tümünü temizle
                </button>
              </div>
            )}

            <div className="gm-resultline">
              <span className="gm-count" aria-live="polite">
                {total === 0
                  ? 'Eşleşen oyun yok'
                  : `${(page * pageSize + 1).toLocaleString('tr-TR')}–${Math.min((page + 1) * pageSize, total).toLocaleString('tr-TR')} / ${total.toLocaleString('tr-TR')} oyun`}
              </span>
              <span className="gm-count-hint">
                <ArrowUp size={12} /> Sıra numarası oyunun sitedeki yerini belirler.
              </span>
            </div>
          </section>

          {loading ? (
            <div className="gm-state"><Loader2 size={26} className="animate-spin" /><p>Katalog yükleniyor…</p></div>
          ) : error ? (
            <div className="gm-state is-error">
              <CircleSlash size={26} />
              <p>{error}</p>
              <button type="button" onClick={loadGames} className="admin-btn-secondary">Yeniden Dene</button>
            </div>
          ) : (
            <div className={`gm-workspace ${showcaseOpen ? '' : 'is-rail'}`}>
              <section className="gm-catalog" aria-label="Oyun kataloğu">
                {filtered.length === 0 ? (
                  <div className="gm-state">
                    <Gamepad2 size={26} />
                    <h2>Oyun bulunamadı</h2>
                    <p>Seçili süzgeçlerle eşleşen bir oyun yok. Aramayı ya da filtreleri değiştirin.</p>
                    {activeFilters.length > 0 && (
                      <button type="button" onClick={clearFilters} className="admin-btn-secondary">
                        <RotateCcw size={13} className="inline mr-1.5" /> Süzgeçleri temizle
                      </button>
                    )}
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="gm-grid">
                    {filtered.map((g, index) => (
                      <article
                        key={g.id}
                        className={`gm-card ${g.featured ? 'is-featured' : ''} ${g.active ? '' : 'is-off'}`}
                      >
                        <div className="gm-card-art">
                          {g.image ? (
                            <MediaThumb src={g.image} />
                          ) : (
                            <div className="gm-card-art-empty"><Gamepad2 size={30} /></div>
                          )}
                          <div className="gm-card-tags">
                            {g.featured && (
                              <span className="gm-tag featured" title="Öne çıkanlar şeridinde">
                                <Star size={10} fill="currentColor" />
                              </span>
                            )}
                            {g.hot && (
                              <span className="gm-tag hot" title="Popüler şeridinde">
                                <Flame size={10} fill="currentColor" />
                              </span>
                            )}
                            {g.new && (
                              <span className="gm-tag new" title="Yeni eklenenler şeridinde">
                                <Sparkles size={10} fill="currentColor" />
                              </span>
                            )}
                          </div>
                          <span className={`gm-live ${g.active ? 'on' : 'off'}`}>
                            {g.active ? 'Aktif' : 'Kapalı'}
                          </span>
                          <div className="gm-card-actions">
                            <button
                              type="button"
                              onClick={() => toggle(g, 'active')}
                              className={`gm-card-btn ${g.active ? 'danger' : 'success'}`}
                              aria-label={g.active ? `${g.name} oyununu devre dışı bırak` : `${g.name} oyununu etkinleştir`}
                              title={g.active ? 'Devre dışı bırak' : 'Etkinleştir'}
                            >
                              {g.active ? <PowerOff size={16} /> : <Power size={16} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => openEdit(g)}
                              className="gm-card-btn primary"
                              aria-label={`${g.name} oyununu düzenle`}
                              title="Düzenle"
                            >
                              <Edit3 size={16} />
                            </button>
                          </div>
                        </div>
                        <div className="gm-card-body">
                          <h3 title={g.name}>{g.name}</h3>
                          {/* RTP burada çizilmez: sağlayıcıların çoğu
                              katalogda böyle bir alan vermiyor ve gösterilen
                              sayı kayıt varsayılanıydı — oyunun ödemesini de
                              belirlemez (bkz. Ayarlar → Oyun API). */}
                          <div className="gm-card-meta">
                            <span title={g.provider || 'Manuel'}>{g.provider || 'Manuel'}</span>
                          </div>
                          {sourceBadges(g)}
                          <div className="gm-card-foot">
                            {flagButtons(g)}
                            {orderControls(g, index)}
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="gm-list" role="table" aria-label="Oyun listesi">
                    <div className="gm-list-head" role="row">
                      <span role="columnheader">Oyun</span>
                      <span role="columnheader">Kaynak</span>
                      <span role="columnheader">Vitrin</span>
                      <span role="columnheader">Sıra</span>
                      <span role="columnheader">İşlem</span>
                    </div>
                    {filtered.map((g, index) => (
                      <div
                        key={g.id}
                        role="row"
                        className={`gm-row ${g.active ? '' : 'is-off'}`}
                      >
                        <div className="gm-row-main" role="cell">
                          {g.image ? (
                            <MediaThumb src={g.image} />
                          ) : (
                            <span className="gm-row-thumb-empty"><Gamepad2 size={16} /></span>
                          )}
                          <div className="gm-row-copy">
                            <strong title={g.name}>{g.name}</strong>
                            <span>
                              {g.provider || 'Manuel'}
                              <i className={`gm-dot ${g.active ? 'on' : 'off'}`} />
                              {g.active ? 'Aktif' : 'Kapalı'}
                            </span>
                          </div>
                        </div>
                        <div className="gm-row-src" role="cell">{sourceBadges(g)}</div>
                        <div role="cell">{flagButtons(g)}</div>
                        <div role="cell">{orderControls(g, index)}</div>
                        <div className="gm-row-actions" role="cell">
                          <button
                            type="button"
                            onClick={() => toggle(g, 'active')}
                            className={`gm-row-btn ${g.active ? 'danger' : 'success'}`}
                            aria-label={g.active ? `${g.name} oyununu devre dışı bırak` : `${g.name} oyununu etkinleştir`}
                            title={g.active ? 'Devre dışı bırak' : 'Etkinleştir'}
                          >
                            {g.active ? <PowerOff size={14} /> : <Power size={14} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(g)}
                            className="gm-row-btn"
                            aria-label={`${g.name} oyununu düzenle`}
                            title="Düzenle"
                          >
                            <Edit3 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {total > PAGE_SIZES[0] && (
                  <div className="gm-pager">
                    <button
                      type="button"
                      disabled={page === 0 || loading}
                      onClick={() => setPage((current) => Math.max(0, current - 1))}
                      className="admin-btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
                    >
                      <ChevronLeft size={14} /> Önceki
                    </button>
                    <span aria-live="polite">Sayfa {page + 1} / {pageCount}</span>
                    <button
                      type="button"
                      disabled={page + 1 >= pageCount || loading}
                      onClick={() => setPage((current) => current + 1)}
                      className="admin-btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
                    >
                      Sonraki <ChevronRight size={14} />
                    </button>
                    <PageSizeSelect
                      value={pageSize}
                      disabled={loading}
                      onChange={(size) => {
                        setPage(0);
                        setPageSize(size);
                        writePageSize('games', size);
                      }}
                    />
                  </div>
                )}
              </section>

              <aside className={`gm-showcase ${showcaseOpen ? '' : 'is-collapsed'}`}
                     aria-labelledby="showcase-games-heading">
                {showcaseOpen ? (
                  <button type="button" className="gm-showcase-head"
                          onClick={toggleShowcasePanel} aria-expanded>
                    <span className="gm-showcase-icon"><Star size={16} fill="currentColor" /></span>
                    <span className="gm-showcase-copy">
                      <h2 id="showcase-games-heading">Ana Sayfa Vitrini</h2>
                      <p>Şeritlerde hangi oyun, hangi sırayla çıkacak.</p>
                    </span>
                    <ChevronDown size={17} className="gm-panel-chevron open" />
                  </button>
                ) : (
                  /* Kapalıyken panel ince bir dikey şeride iner; oyun listesi
                     boşalan genişliği kullanır. */
                  <button type="button" className="gm-showcase-rail"
                          onClick={toggleShowcasePanel} aria-expanded={false}
                          title="Ana Sayfa Vitrini panelini aç">
                    <ChevronLeft size={16} className="gm-showcase-rail-arrow" />
                    <span className="gm-showcase-icon"><Star size={14} fill="currentColor" /></span>
                    <span className="gm-showcase-rail-label" id="showcase-games-heading">
                      Ana Sayfa Vitrini
                    </span>
                    <span className="gm-showcase-rail-count">
                      {(showcaseCounts.featured || 0) + (showcaseCounts.hot || 0)
                        + (showcaseCounts.new || 0)}
                    </span>
                  </button>
                )}
                {showcaseOpen && (
                  <>
                    <div className="gm-showcase-tabs" role="tablist" aria-label="Vitrin şeridi">
                      {showcaseTabs.map((tab) => {
                        const Icon = tab.icon;
                        const count = showcaseCounts[tab.flag] || 0;
                        return (
                          <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={showcaseTab === tab.id}
                            onClick={() => setShowcaseTab(tab.id)}
                            className={`gm-showcase-tab ${showcaseTab === tab.id ? 'is-active' : ''}`}
                          >
                            <Icon size={13} /> {tab.name}
                            <span className="gm-showcase-count">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="gm-showcase-hint">{showcase.hint}</p>
                    <div className="gm-showcase-list">
                      {showcaseGames.map((game, index) => (
                        <div className="gm-showcase-row" key={game.id}>
                          {game.image ? (
                            <MediaThumb src={game.image} />
                          ) : (
                            <div className="gm-showcase-thumb-empty"><Gamepad2 size={17} /></div>
                          )}
                          <div className="gm-showcase-copy-row">
                            <strong title={game.name}>{game.name}</strong>
                            <span>{game.provider || 'Manuel'}</span>
                          </div>
                          <label>
                            <span className="sr-only">{game.name} sıra numarası</span>
                            <input
                              type="number"
                              min="1"
                              max={showcaseGames.length}
                              defaultValue={index + 1}
                              disabled={gameOrderBusy}
                              onBlur={(event) => setShowcaseOrder(game, event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') event.currentTarget.blur();
                              }}
                            />
                          </label>
                          <div className="gm-showcase-move">
                            <button
                              type="button"
                              onClick={() => moveShowcase(game, -1)}
                              disabled={gameOrderBusy || index === 0}
                              aria-label={`${game.name} yukarı taşı`}
                            >
                              <ArrowUp size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveShowcase(game, 1)}
                              disabled={gameOrderBusy || index === showcaseGames.length - 1}
                              aria-label={`${game.name} aşağı taşı`}
                            >
                              <ArrowDown size={12} />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="gm-showcase-remove"
                            onClick={() => toggle(game, showcase.flag)}
                            aria-label={`${game.name} oyununu ${showcase.name} şeridinden çıkar`}
                            title="Şeritten çıkar"
                          >
                            <X size={13} />
                          </button>
                        </div>
                      ))}
                      {showcaseGames.length === 0 && (
                        <div className="gm-showcase-empty">
                          <showcase.icon size={22} />
                          <p>
                            Bu şerit için oyun seçilmedi; şu anda otomatik dolduruluyor.{' '}
                            {showcase.auto}
                          </p>
                          <p>
                            Soldaki oyun kartlarındaki{' '}
                            <strong>{showcase.id === 'featured' ? 'yıldız' : showcase.name}</strong>{' '}
                            düğmesiyle oyun ekleyebilirsiniz.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </aside>
            </div>
          )}
        </>
      )}

      {view === 'providers' && (
        <div className="gm-provider-grid">
          {providers.map((p, index) => (
            <article key={p.id} className={`gm-provider ${p.active ? '' : 'is-off'}`}>
              <div className="gm-provider-head">
                {p.logo ? (
                  <img src={resolveApiAssetUrl(p.logo)} alt="" loading="lazy" />
                ) : (
                  <span className="gm-provider-logo-empty"><Server size={18} /></span>
                )}
                <div className="gm-provider-id">
                  <strong title={p.name}>{p.name}</strong>
                  <span>#{p.provider_id} · {p.api_provider || 'manuel'}</span>
                </div>
                <div className="gm-order gm-provider-order">
                  <label>
                    <span className="sr-only">{p.name} sıra numarası</span>
                    <input
                      key={`${p.id}-${index}`}
                      type="number"
                      min="1"
                      max={providers.length}
                      defaultValue={index + 1}
                      disabled={providerOrderBusy}
                      onBlur={(event) => {
                        const requested = Math.max(
                          1,
                          Math.min(providers.length, Number(event.currentTarget.value) || index + 1),
                        );
                        event.currentTarget.value = String(requested);
                        setProviderOrder(p, requested);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => moveProvider(p, -1)}
                    disabled={providerOrderBusy || index === 0}
                    aria-label={`${p.name} sağlayıcısını yukarı taşı`}
                    title="Yukarı taşı"
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveProvider(p, 1)}
                    disabled={providerOrderBusy || index === providers.length - 1}
                    aria-label={`${p.name} sağlayıcısını aşağı taşı`}
                    title="Aşağı taşı"
                  >
                    <ArrowDown size={13} />
                  </button>
                </div>
              </div>

              <div className="gm-provider-badges">
                <span className={`badge-pill ${p.status === 1 ? 'badge-green' : 'badge-gray'}`}
                      title="Sağlayıcının API tarafındaki durumu">
                  API: {p.status === 1 ? 'Açık' : 'Kapalı'}
                </span>
                <span className={`badge-pill ${p.active ? 'badge-green' : 'badge-gray'}`}
                      title="Sağlayıcının oyunları sitede görünüyor mu">
                  Sitede: {p.active ? 'Aktif' : 'Pasif'}
                </span>
                <span className="gm-provider-sync">
                  Son senkron: {p.synced_at ? new Date(p.synced_at).toLocaleString('tr-TR') : '—'}
                </span>
              </div>

              <div className="gm-provider-actions">
                <button
                  type="button"
                  onClick={() => syncProviderGames(p)}
                  disabled={!!syncing || p.api_provider === 'scorpio'}
                  className="admin-btn-primary text-xs inline-flex items-center gap-1.5"
                >
                  {syncing === `gold-games-${p.provider_id}` || syncing === 'drakon-games'
                    || syncing === `oroplay-games-${p.provider_code}`
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Download size={12} />}
                  Oyunları Çek
                </button>
                <button type="button" onClick={() => bulkProvider(p.name, true)}
                        className="admin-btn-secondary text-xs"
                        title="Bu sağlayıcının tüm oyunlarını aktif yap">Tümünü Aç</button>
                <button type="button" onClick={() => bulkProvider(p.name, false)}
                        className="admin-btn-secondary text-xs"
                        title="Bu sağlayıcının tüm oyunlarını pasif yap">Tümünü Kapat</button>
                <button
                  type="button"
                  onClick={() => toggleProvider(p)}
                  className={`gm-provider-power ${p.active ? 'is-on' : ''}`}
                  aria-pressed={p.active}
                  title={p.active ? 'Sağlayıcıyı sitede gizle' : 'Sağlayıcıyı sitede göster'}
                >
                  {p.active ? <Power size={13} /> : <PowerOff size={13} />}
                </button>
              </div>
            </article>
          ))}
          {providers.length === 0 && (
            <div className="gm-state gm-span">
              <Server size={26} />
              <h2>Sağlayıcı yok</h2>
              <p>
                Oyun Kontrol → Oyun Servisleri bölümündeki "Sağlayıcıları Çek" ile
                API'den senkronize edin.
              </p>
            </div>
          )}
        </div>
      )}

      {view === 'labels' && (
        <section className="gm-labels-wrap">
          <header className="gm-section-head">
            <span className="gm-panel-icon"><ImageIcon size={15} /></span>
            <div>
              <h2>Vitrin Çipleri</h2>
              <p>
                Slot ve Canlı Casino sayfalarındaki sağlayıcı filtre şeridi ve ana
                sayfanın Popüler Sağlayıcılar bölümü. Ad boş bırakılırsa sağlayıcı
                kodundan otomatik türetilir; logo yüklenirse çipte yazı yerine logo
                görünür.
              </p>
              <p>
                <strong>Sıra:</strong> 1, 2, 3… verilen çipler başa gelir; 0 sırasızdır
                ve alfabetik olarak arkaya dizilir. Ana sayfa şeridi ilk 22 çipi
                gösterir — orada görünmesini istediğiniz sağlayıcıya sıra verin.
                Sitede görünmeyen (aktif oyunu olmayan) sağlayıcılar listenin
                sonundadır; logoyu oyuncunun gördüğü satıra yükleyin.
              </p>
            </div>
          </header>
          <div className="gm-labels">
            {labels.map((item) => {
              const draft = { ...item, ...(labelDrafts[item.slug] || {}) };
              const dirty = Boolean(labelDrafts[item.slug]);
              const patch = (values) => setLabelDrafts((current) => ({
                ...current,
                [item.slug]: { ...(current[item.slug] || {}), ...values },
              }));
              return (
                <article key={item.slug} className={`gm-label ${dirty ? 'is-dirty' : ''} ${item.visible_count === 0 ? 'is-offsite' : ''}`}>
                  <div className="gm-label-preview">
                    <span className="gm-label-chip">
                      {draft.logo ? (
                        <img src={resolveApiAssetUrl(draft.logo)} alt={draft.name || item.slug} />
                      ) : (draft.name || item.slug)}
                    </span>
                    <code>{item.slug}</code>
                    {/* Sitede görünen oyun sayısı vitrin sorgusuyla sayılır;
                        adı benzer iki kayıttan (ör. "pragmatic" ve "Pragmatic
                        Play") hangisinin oyuncuya göründüğünü bu ayırır. */}
                    {item.visible_count === 0 ? (
                      <span className="gm-label-offsite">Sitede görünmüyor · aktif oyunu yok</span>
                    ) : (
                      <span className="gm-label-count">Sitede {item.visible_count ?? item.game_count} oyun</span>
                    )}
                  </div>

                  <div className="gm-label-fields">
                    <label className="gm-field">
                      <span>Görünen ad</span>
                      <input
                        value={draft.name || ''}
                        onChange={(event) => patch({ name: event.target.value })}
                        className="admin-input"
                        placeholder={item.slug}
                      />
                    </label>

                    <label className="gm-field gm-field-wide">
                      <span>Logo</span>
                      <div className="gm-label-logo">
                        <input
                          value={draft.logo || ''}
                          onChange={(event) => patch({ logo: event.target.value })}
                          className="admin-input"
                          placeholder="Logo adresi ya da yükleyin"
                        />
                        <label className="gm-upload" title="Logo yükle">
                          {labelBusy === item.slug
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Upload size={13} />}
                          <span className="sr-only">{item.slug} için logo yükle</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                            className="sr-only"
                            disabled={labelBusy === item.slug}
                            onChange={(event) => {
                              uploadLabelLogo(item.slug, event.target.files?.[0]);
                              event.target.value = '';
                            }}
                          />
                        </label>
                        {draft.logo && (
                          <button
                            type="button"
                            onClick={() => patch({ logo: '' })}
                            className="icon-btn ban"
                            title="Logoyu kaldır"
                            aria-label={`${item.slug} logosunu kaldır`}
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </label>

                    <label className="gm-field gm-field-narrow">
                      <span>Sıra</span>
                      <input
                        type="number"
                        min="0"
                        value={draft.order ?? 0}
                        onChange={(event) => patch({ order: event.target.value })}
                        className="admin-input"
                      />
                    </label>
                  </div>

                  <div className="gm-label-foot">
                    <label className="gm-check">
                      <input
                        type="checkbox"
                        checked={!!draft.hidden}
                        onChange={(event) => patch({ hidden: event.target.checked })}
                      />
                      Vitrinde gizle
                    </label>
                    <button
                      type="button"
                      disabled={!dirty || labelBusy === item.slug}
                      onClick={() => saveLabel(item.slug)}
                      className="admin-btn-primary text-xs disabled:opacity-40"
                    >
                      {labelBusy === item.slug ? '…' : dirty ? 'Kaydet' : 'Kaydedildi'}
                    </button>
                  </div>
                </article>
              );
            })}
            {labels.length === 0 && (
              <div className="gm-state">
                <ImageIcon size={26} />
                <h2>Sağlayıcı bulunamadı</h2>
                <p>Katalogda çip üretilecek bir sağlayıcı yok.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {view === 'sessions' && (
        <section className="gm-sessions">
          {sessions.map((s) => (
            <article key={s.id} className="gm-session">
              <span className="gm-session-icon"><Radio size={16} /></span>
              <div className="gm-session-copy">
                <strong>{s.username}</strong>
                <span>{s.game_name} · {s.provider_name}</span>
              </div>
              <div className="gm-session-amount">
                <em>Aktarılan</em>
                <b>{money(s.transferred_in)}</b>
              </div>
              <div className="gm-session-time">
                <em>Başlangıç</em>
                <b>{new Date(s.created_at).toLocaleString('tr-TR')}</b>
              </div>
              <button type="button" onClick={() => closeSession(s)}
                      className="admin-btn-secondary text-xs inline-flex items-center gap-1.5">
                <LogOut size={12} /> Kapat & Bakiyeyi Al
              </button>
            </article>
          ))}
          {sessions.length === 0 && (
            <div className="gm-state">
              <Radio size={26} />
              <h2>Aktif oyun oturumu yok</h2>
              <p>Transfer cüzdanlı bir sağlayıcıda oyun açıldığında oturumlar burada listelenir.</p>
            </div>
          )}
        </section>
      )}

      {/* Oyun düzenleme penceresi — uzun form üç sekmeye ayrılır, Kaydet
          hepsini tek istekte uygular. */}
      {modal && (
        <div className="gm-modal-backdrop" onClick={() => setModal(null)} role="presentation">
          <div
            className="gm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`${form.name} oyununu düzenle`}
          >
            <header className="gm-modal-head">
              {form.image_override || form.api_image ? (
                <MediaThumb src={form.image_override || form.api_image} />
              ) : (
                <span className="gm-modal-thumb-empty"><Gamepad2 size={18} /></span>
              )}
              <div className="gm-modal-title">
                <h3 title={form.name}>{form.name}</h3>
                <span>{form.provider || 'Manuel'} · {form.reel_provider_name || form.api_provider || '—'}</span>
              </div>
              <button type="button" onClick={() => setModal(null)} className="gm-modal-close"
                      aria-label="Pencereyi kapat">
                <X size={16} />
              </button>
            </header>

            <nav className="gm-modal-tabs" role="tablist" aria-label="Oyun ayar bölümleri">
              <button type="button" role="tab" aria-selected={modalTab === 'settings'}
                      onClick={() => setModalTab('settings')}
                      className={`gm-modal-tab ${modalTab === 'settings' ? 'is-active' : ''}`}>
                <Settings2 size={14} /> Ayarlar
              </button>
              <button type="button" role="tab" aria-selected={modalTab === 'media'}
                      onClick={() => setModalTab('media')}
                      className={`gm-modal-tab ${modalTab === 'media' ? 'is-active' : ''}`}>
                <ImageIcon size={14} /> Görseller
                {form.image_override && <span className="gm-modal-dot" title="Özel görsel kullanılıyor" />}
              </button>
              <button type="button" role="tab" aria-selected={modalTab === 'link'}
                      onClick={() => setModalTab('link')}
                      className={`gm-modal-tab ${modalTab === 'link' ? 'is-active' : ''}`}>
                <Link2 size={14} /> Reel / Demo
                {twinsDirty && <span className="gm-modal-dot is-warn" title="Kaydedilmemiş eşleştirme değişikliği" />}
              </button>
            </nav>

            <div className="gm-modal-body">
              {modalTab === 'settings' && (
                <div className="gm-modal-pane">
                  <div className="gm-form-grid">
                    <label className="gm-field">
                      <span>Oyun adı</span>
                      <input value={form.name} className="admin-input" readOnly />
                    </label>
                    <label className="gm-field">
                      <span>Sağlayıcı</span>
                      <input value={form.provider} className="admin-input" readOnly />
                    </label>
                    <label className="gm-field">
                      <span>Kategori</span>
                      <select
                        value={form.category}
                        onChange={(e) => setForm({ ...form, category: e.target.value })}
                        className="admin-input"
                      >
                        {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </label>
                    <label className="gm-field">
                      <span>Sıra</span>
                      <input
                        type="number"
                        value={form.order}
                        onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                        className="admin-input"
                      />
                    </label>
                  </div>

                  <div className="gm-switches">
                    {[
                      ['active', 'Sitede aktif', 'Kapalıyken oyun portalda hiç görünmez.'],
                      ['featured', 'Öne çıkan', 'Ana sayfadaki öne çıkanlar şeridine girer.'],
                      ['hot', 'Popüler', 'Ana sayfadaki popüler şeridine girer.'],
                      ['new', 'Yeni', 'Yeni eklenenler şeridine girer.'],
                    ].map(([key, label, hint]) => (
                      <label key={key} className={`gm-switch ${form[key] ? 'is-on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!!form[key]}
                          onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                        />
                        <span className="gm-switch-track" aria-hidden="true"><i /></span>
                        <span className="gm-switch-copy">
                          <strong>{label}</strong>
                          <em>{hint}</em>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {modalTab === 'media' && (
                <div className="gm-modal-pane">
                  <p className="gm-note">
                    API görseli değişmeden saklanır. Özel görsel boşsa oyun otomatik olarak
                    API görselini kullanır; senkron özel görsele dokunmaz.
                  </p>
                  <div className="gm-media">
                    <div className="gm-media-card">
                      <div className="gm-media-head">
                        <span>API Görseli</span>
                        <span className="badge-pill badge-gray">Varsayılan</span>
                      </div>
                      <div className="gm-media-frame">
                        {form.api_image ? (
                          <MediaThumb src={form.api_image} alt="API oyun görseli" />
                        ) : (
                          <ImageIcon size={28} />
                        )}
                      </div>
                      <input
                        value={form.api_image || ''}
                        className="admin-input"
                        readOnly
                        title={form.api_image || 'API görseli bulunmuyor'}
                      />
                    </div>

                    <div className="gm-media-card is-custom">
                      <div className="gm-media-head">
                        <span>Özel Görsel</span>
                        <span className={`badge-pill ${form.image_override ? 'badge-blue' : 'badge-gray'}`}>
                          {form.image_override ? 'Özel aktif' : 'API kullanılıyor'}
                        </span>
                      </div>
                      <div className="gm-media-frame">
                        {(form.image_override || form.api_image) ? (
                          <MediaThumb
                            src={form.image_override || form.api_image}
                            alt="Sitede kullanılacak oyun görseli"
                          />
                        ) : (
                          <ImageIcon size={28} />
                        )}
                      </div>
                      <input
                        value={form.image_override || ''}
                        onChange={(event) => setForm({ ...form, image_override: event.target.value })}
                        className="admin-input"
                        placeholder="Özel görsel adresi (opsiyonel)"
                        disabled={uploadingImage}
                      />
                      <div className="gm-media-actions">
                        <label className={`admin-btn-secondary gm-media-upload ${uploadingImage ? 'is-busy' : ''}`}>
                          {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          {uploadingImage ? 'Yükleniyor…' : 'Görsel / Video Yükle'}
                          <input
                            type="file"
                            accept={`${GAME_ARTWORK_ACCEPT},.svg`}
                            className="sr-only"
                            disabled={uploadingImage}
                            onChange={uploadGameImage}
                          />
                        </label>
                        {form.image_override && (
                          <button
                            type="button"
                            onClick={() => setForm((current) => ({ ...current, image_override: '' }))}
                            className="admin-btn-secondary text-red-400 inline-flex items-center gap-1.5"
                            disabled={uploadingImage}
                          >
                            <Trash2 size={13} /> API'ye Dön
                          </button>
                        )}
                      </div>
                      <p className="gm-hint">
                        JPG, PNG, GIF, WEBP, SVG · hareketli kart için GIF, MP4 ya da WEBM ·
                        en fazla {UPLOAD_MAX_LABEL}. Hareketli kart sitede sessiz ve döngülü
                        oynar; yalnızca oyun görselinde kullanılabilir.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {modalTab === 'link' && (
                <div className="gm-modal-pane">
                  <div className="gm-subsection">
                    <h4>Sağlayıcı Entegrasyonu</h4>
                    <div className="gm-form-grid">
                      <label className="gm-field">
                        <span>API sağlayıcı (reel)</span>
                        <input
                          value={form.reel_provider_name || form.api_provider || '—'}
                          className="admin-input"
                          readOnly
                        />
                      </label>
                      <label className="gm-field">
                        <span>Provider ID</span>
                        <select value={form.provider_id} className="admin-input" disabled>
                          <option value="">-</option>
                          {providers.map((p) => (
                            <option key={p.id} value={p.provider_id}>#{p.provider_id} · {p.name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="gm-field gm-field-wide">
                        <span>Oyun kodu (gameCode)</span>
                        <input value={form.game_id} className="admin-input font-mono" readOnly />
                      </label>
                    </div>
                  </div>

                  <div className="gm-subsection">
                    <h4>Reel / Demo Eşleştirmesi</h4>
                    <p className="gm-note">
                      Reel (görünen) kaydı ve demonun hangi API'den açılacağını seçin. Seçim{' '}
                      <strong>Kaydet'e basınca</strong> uygulanır ve kilitlenir; oyunlar yeniden
                      çekilse bile senkron ezmez.
                    </p>
                    {twinsBusy ? (
                      <div className="gm-hint">
                        <Loader2 size={13} className="inline animate-spin mr-1" /> Yükleniyor…
                      </div>
                    ) : twins.length <= 1 ? (
                      <div className="gm-hint">
                        {twinsKey ? (
                          <>
                            Bu oyunun başka bir API'de kopyası bulunamadı. Kopya olması
                            gerekiyorsa önce ilgili API'yi senkronize edip{' '}
                            <strong>Oto Eşleştir</strong>'i çalıştırın.
                          </>
                        ) : (
                          <>
                            Bu kaydın <strong>eşleştirme anahtarı yok</strong>, bu yüzden
                            kopyaları aranamıyor. Oyunlar listesindeki{' '}
                            <strong>Anahtarları Üret</strong> düğmesini bir kez çalıştırın.
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="gm-twins">
                        {!twinsPrimaryId && !twinDraft.unlink && twinsSplit && (
                          <div className="gm-alert is-info">
                            <div>
                              Bu oyunun eşleşmesi çözüldü: {twins.length} kopya sitede ayrı ayrı
                              görünür ve senkron ya da <strong>Oto Eşleştir</strong> onları yeniden
                              birleştirmez. Birleştirmek için reel olmasını istediğiniz kaydın
                              yanındaki <strong>Reel yap</strong> düğmesini kullanın.
                            </div>
                          </div>
                        )}
                        {!twinsPrimaryId && !twinDraft.unlink && !twinsSplit && (
                          /* Kopyalar henüz birleştirilmemiş: ikisi de sitede ayrı ayrı
                             görünür. Toplu işlemi çalıştırmak istemeyen yönetici
                             yalnızca bu oyunu birleştirebilsin. */
                          <div className="gm-alert is-amber">
                            <div>
                              Bu oyunun {twins.length} kopyası var ve henüz birleştirilmemiş —
                              sitede {twins.length} kez görünür. Reel olmasını istediğiniz kaydın
                              yanındaki <strong>Reel yap</strong> düğmesi yalnızca bu oyunu
                              birleştirir; demo verebilen kopya demo kaynağı olarak bağlanır.
                            </div>
                          </div>
                        )}
                        {twinsDirty && (
                          <div className="gm-alert is-info">
                            <div>
                              Eşleştirme değiştirildi — <strong>Kaydet</strong>'e basana kadar
                              uygulanmaz.
                            </div>
                            <button type="button" onClick={resetTwinDraft}
                                    className="admin-btn-secondary text-xs inline-flex items-center gap-1.5">
                              <RotateCcw size={12} /> Geri al
                            </button>
                          </div>
                        )}
                        {twins.map((t) => {
                          // Rozetler taslaktan çizilir (henüz kaydedilmemiş olabilir).
                          const isPrimary = twinsPrimaryId === t.id;
                          const isDemoSource = !!twinsPrimaryId && (
                            twinDraft.demoGameId === t.id
                            || (isPrimary && !twinDraft.demoGameId && t.can_serve_demo));
                          return (
                            <div key={t.id}
                                 className={`gm-twin ${isPrimary ? 'is-primary' : ''}`}>
                              <div className="gm-twin-copy">
                                <strong>{t.api_provider_name}</strong>
                                <div className="gm-twin-badges">
                                  <span className={`badge-pill ${t.active ? 'badge-green' : 'badge-gray'}`}>
                                    {t.active ? 'Aktif' : 'Pasif'}
                                  </span>
                                  {isPrimary && <span className="badge-pill badge-blue">Reel</span>}
                                  {isDemoSource && <span className="badge-pill badge-green">Demo</span>}
                                  {!t.can_serve_demo && (
                                    <span className="gm-twin-note">
                                      demo yok{t.demo_note ? ` (${t.demo_note})` : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="gm-twin-actions">
                                {!isPrimary && (
                                  <button type="button" disabled={twinsBusy}
                                          onClick={() => makeReel(t.id)}
                                          className="admin-btn-secondary text-xs">
                                    Reel yap
                                  </button>
                                )}
                                {!isPrimary && t.can_serve_demo && twinsPrimaryId && !isDemoSource && (
                                  <button type="button" disabled={twinsBusy}
                                          onClick={() => setDemoSource(t.id)}
                                          className="admin-btn-secondary text-xs">
                                    Demo kaynağı yap
                                  </button>
                                )}
                                {isPrimary && t.can_serve_demo && twinDraft.demoGameId && (
                                  <button type="button" disabled={twinsBusy}
                                          onClick={() => setDemoSource(null)}
                                          className="admin-btn-secondary text-xs">
                                    Demoyu kendinden ver
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {twinOriginal.primaryId && !twinDraft.unlink && (
                          <button type="button" disabled={twinsBusy}
                                  onClick={unlinkGame}
                                  className="admin-btn-secondary text-xs text-red-400 inline-flex items-center gap-1.5 self-start">
                            <X size={12} /> Eşleşmeyi çöz (kopyaları ayır)
                          </button>
                        )}
                        {twinDraft.unlink && (
                          <div className="gm-alert is-danger">
                            <div>
                              Eşleşme çözülecek: kopyalar ayrı ayrı görünür hâle gelir ve
                              senkron sonrası yeniden birleşmez.
                            </div>
                            <button type="button" onClick={resetTwinDraft}
                                    className="admin-btn-secondary text-xs inline-flex items-center gap-1.5">
                              <RotateCcw size={12} /> Geri al
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <footer className="gm-modal-foot">
              {twinsDirty && (
                <span className="gm-modal-flag">
                  <Link2 size={13} /> Eşleştirme değişikliği kaydedilecek
                </span>
              )}
              <button type="button" onClick={() => setModal(null)} className="admin-btn-secondary">
                Vazgeç
              </button>
              <button
                type="button"
                disabled={busy || uploadingImage}
                onClick={save}
                className="admin-btn-primary inline-flex items-center gap-2"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Kaydet
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;
